import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Banner } from '@/components/ui/Banner';
import { Skeleton } from '@/components/ui/Skeleton';
import { QuestionnairePicker } from '@/components/questionnaires/QuestionnairePicker';
import { useFormsTenantStatus, useSendQuestionnaire } from '@/hooks/useClinioForms';
import { useFormsTemplateList } from '@/hooks/useFormsTemplates';
import { usePatientAppointments } from '@/hooks/usePatientAppointments';
import { usePatientFormsAssignments } from '@/hooks/useClinioForms';
import { toQuestionnairePickerItems } from '@/lib/questionnairePickerLabel';
import { endOfChosenClinicDay } from '@/lib/questionnaireDue';
import { supabase } from '@/lib/supabase';
import { useQuery } from '@tanstack/react-query';

interface SendFormsAssignmentModalProps {
  patientId: string;
  clinicId: string;
  onClose: () => void;
}

export function SendFormsAssignmentModal({
  patientId,
  clinicId,
  onClose,
}: SendFormsAssignmentModalProps) {
  const { t, i18n } = useTranslation();
  const tenantStatus = useFormsTenantStatus(clinicId);
  const templatesQuery = useFormsTemplateList(clinicId, i18n.language);
  const assignments = usePatientFormsAssignments(patientId, clinicId);
  const appointments = usePatientAppointments(patientId, clinicId);
  const sendMutation = useSendQuestionnaire();
  const patientQuery = useQuery({
    queryKey: ['patient-brief', patientId, clinicId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('patients')
        .select('first_name, last_name, email')
        .eq('id', patientId)
        .eq('clinic_id', clinicId)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
  });

  const items = useMemo(
    () =>
      toQuestionnairePickerItems(
        [...(templatesQuery.data?.platform ?? []), ...(templatesQuery.data?.clinic ?? [])],
        i18n.language,
        t('forms.unnamed_questionnaire'),
        (count) => t('forms.picker.questions', { count }),
      ),
    [i18n.language, t, templatesQuery.data],
  );
  const pendingIds = useMemo(
    () =>
      (assignments.data ?? [])
        .filter((row) => row.status === 'sent' || row.status === 'reminded')
        .map((row) => row.forms_template_id),
    [assignments.data],
  );
  const recentIds = useMemo(() => {
    const seen = new Set<string>();
    const ids: string[] = [];
    for (const row of assignments.data ?? []) {
      if (seen.has(row.forms_template_id)) continue;
      seen.add(row.forms_template_id);
      ids.push(row.forms_template_id);
      if (ids.length >= 8) break;
    }
    return ids;
  }, [assignments.data]);

  const tenantKnown = tenantStatus.isSuccess || tenantStatus.isError;
  const tenantReady = tenantStatus.data?.status === 'active';

  if (!tenantKnown || templatesQuery.isLoading) {
    return (
      <div className="fixed inset-0 z-40 flex justify-end bg-ds-scrim">
        <div className="flex h-full w-full max-w-[560px] flex-col bg-ds-card p-s5 nav:max-w-[560px]">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="mt-s4 h-10 w-full" />
          <Skeleton className="mt-s3 h-16 w-full" />
          <Skeleton className="mt-s3 h-16 w-full" />
        </div>
      </div>
    );
  }

  if (!tenantReady) {
    return (
      <QuestionnairePicker
        open
        title={t('forms.modal.title')}
        clinicId={clinicId}
        items={[]}
        tenantReady={false}
        tenantWarning={t('forms.modal.tenant_not_active')}
        onClose={onClose}
        onConfirm={() => undefined}
      />
    );
  }

  return (
    <>
      {sendMutation.isError ? (
        <Banner tone="error" className="sr-only">
          {sendMutation.error instanceof Error ? sendMutation.error.message : t('forms.modal.error_send')}
        </Banner>
      ) : null}
      <QuestionnairePicker
        open
        title={t('forms.modal.title')}
        clinicId={clinicId}
        items={items}
        pendingIds={pendingIds}
        recentIds={recentIds}
        mode="send"
        patientName={
          patientQuery.data
            ? `${patientQuery.data.first_name} ${patientQuery.data.last_name}`.trim()
            : undefined
        }
        patientEmail={patientQuery.data?.email}
        appointments={appointments.data ?? []}
        defaultLocale={tenantStatus.data?.default_locale ?? 'da-DK'}
        sending={sendMutation.isPending}
        tenantReady
        onClose={onClose}
        onConfirm={async (payload) => {
          if (payload.ids.length === 0) return;
          const due = payload.dueAt ? endOfChosenClinicDay(payload.dueAt).toISOString() : null;
          for (const templateId of payload.ids) {
            await sendMutation.mutateAsync({
              patient_id: patientId,
              template_id: templateId,
              due_at: due,
              locale: payload.locale,
              metadata: payload.appointmentId ? { appointment_id: payload.appointmentId } : undefined,
            });
          }
          onClose();
        }}
      />
    </>
  );
}
